import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import MultiItemSelect from '../../MultiItemSelect';
import { graphql, compose } from 'react-apollo';
import UserTypeSelection from './UserTypeSelection';
import ExistingDirectMessagesList from './ExistingDirectMessagesList';
import { GET_DIRECT_MESSAGEABLE_EMPLOYEES, GET_DIRECT_MESSAGEABLE_PARENTS } from '../../../graphql/queries';
import CreateChannelModal from '../CreateChannelModal';

const MAX_DIRECT_MESSAGE_USERS = 8;

const DirectMessageModal = ({
  isOpen,
  onClose,
  currentCenterId,
  existingDirectMessages,
  onDirectMessageSelect,
  existingChannels,
  employeeLegacyChatId,
  selectChannel,
  // gql props
  directMessageableEmployees: { getChannelMessageableUsers },
  directMessageableParents: { getDirectMessageableParentsAsEmployee },
  ...props
}) => {
  const [selectedUserType, setSelectedUserType] = useState('employee');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterValue, setFilterValue] = useState('');
  const invalidNumberOfUsers = selectedUsers.length === 0 || selectedUsers.length > MAX_DIRECT_MESSAGE_USERS;

  // effects
  // every time the user type changes reset `selectedUsers`
  useEffect(() => {
    setFilterValue('');
    setSelectedUsers([]);
  }, [selectedUserType]);

  // only filtering existing direct messages on first name of the selected users
  const selectedUsersString = selectedUsers.map(u => u.name.split(' ')[0].toLowerCase()).join(' ');
  const filteredUsers = usersArray => usersArray ? usersArray.filter(u => u.name.toLowerCase().includes(filterValue)) : [];

  return (
    <CreateChannelModal
      isOpen={isOpen}
      onClose={() => onClose(existingDirectMessages)}
      title='Direct Messages'
      subtitle='Who would you like to message?'
      buttonText='Create Conversation'
      buttonDisabled={invalidNumberOfUsers}
      newChannelDetails={{
        centerId: parseInt(currentCenterId, 10),
        directMessage: true,
        invitedUserIds: selectedUsers.map(u => ({ id: `${u.id}`, type: selectedUserType }))
      }}
      existingChannels={existingChannels}
      employeeLegacyChatId={employeeLegacyChatId}
      selectChannel={selectChannel}
    >
      <UserTypeSelection
        selectedValue={selectedUserType}
        onSelect={setSelectedUserType}
      />
      <MultiItemSelect
        label={selectedUsers.length > 0 ? `Start a conversation (You can add ${Math.max(MAX_DIRECT_MESSAGE_USERS - selectedUsers.length, 0)} more people)` : 'Start a conversation'}
        filteredItems={selectedUserType === 'employee' ? filteredUsers(getChannelMessageableUsers) : filteredUsers(getDirectMessageableParentsAsEmployee)}
        items={selectedUserType === 'employee' ? getChannelMessageableUsers : getDirectMessageableParentsAsEmployee}
        onFilter={filterValue => setFilterValue(filterValue)}
        onItemSelect={items => {
          if (selectedUsers.length === MAX_DIRECT_MESSAGE_USERS) {
            // removing items, update
            if (items.length < selectedUsers.length) {
              setSelectedUsers(items);
            }
            return;
          }

          setSelectedUsers(items);
        }}
        disableSelection={selectedUsers.length === MAX_DIRECT_MESSAGE_USERS}
      />
      <ExistingDirectMessagesList
        existingMessages={existingDirectMessages.length
          ? existingDirectMessages
            .filter(c => c.name.toLowerCase().includes(selectedUsersString))
            .sort((c1, c2) => new Date(c2.lastMessageSentAt) - new Date(c1.lastMessageSentAt))
          : []
        }
        onDirectMessageSelect={(id, channelDetails) => {
          onClose(existingDirectMessages);
          onDirectMessageSelect(id, channelDetails);
        }}
      />
    </CreateChannelModal>
  );
};

DirectMessageModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  existingDirectMessages: PropTypes.array,
  onDirectMessageSelect: PropTypes.func,
  existingChannels: PropTypes.array
};

export default compose(
  graphql(GET_DIRECT_MESSAGEABLE_EMPLOYEES, {
    name: 'directMessageableEmployees',
    options: props => ({
      fetchPolicy: 'cache-and-network',
      variables: {
        centerId: props.currentCenterId
      }
    })
  }),
  graphql(GET_DIRECT_MESSAGEABLE_PARENTS, {
    name: 'directMessageableParents',
    options: props => ({
      fetchPolicy: 'cache-and-network',
      variables: {
        centerId: props.currentCenterId
      }
    })
  })
)(DirectMessageModal);
